import { computed, ref } from '@nuxtjs/composition-api';
import {
  BillingFeature,
  BillingPeriod,
  PricingPackage,
  ProductKey,
  useGetPricingPackagesQuery,
  useUpgradeCorePlanMutation,
} from '@/gql/generated';
import { useApolloMutation, useApolloQuery } from '@/gql/apolloWrapper';
import useCurrentPlan from '../useCurrentPlan/useCurrentPlan';
import useNotifications from '@bambeehr/use-notifications';
import bam from '@/lib/bam';
import HrGuidanceEvents from '@/modules/HrGuidance/constants/events';
import useCurrentCompany from '../useCurrentCompany';
import useCurrentUser from '../useCurrentUser';
import { saveInLocalStorage } from '@/utils/localStorage';
import useStaffManagement from '@/modules/StaffManagement/hooks/useStaffManagement';
import usePlanAccess from '../usePlanAccess/usePlanAccess';
import { getChargeCycleForBillingPeriod } from '@/constants/Billing';

// Display name mapping for ProductKey values used in the upgrade modal
export const PRODUCT_DISPLAY_NAMES = {
  [ProductKey.Business]: 'Business Account',
  [ProductKey.Lite]: 'Lite',
  [ProductKey.Advisor]: 'Advisor One',
  [ProductKey.Pause]: 'Pause',
  [ProductKey.Storage]: 'Storage',
  [ProductKey.Core]: 'Dedicated',
  [ProductKey.Guidance]: 'Guidance',
  [ProductKey.Elite]: 'Elite',
  [ProductKey.Elite2]: 'Elite 2',
  [ProductKey.Elite3]: 'Elite 3',
  [ProductKey.PremiumPeople]: 'Premium People',
} as const;

let init;

const ELITE_PURCHASED_DURING_THREAD_KEY = 'elite-purchased-during-thread';

const mapEmployeeCountToTier = (employeeCount: number): number => {
  if (employeeCount <= 4) {
    return 1;
  }
  if (employeeCount <= 19) {
    return 2;
  }
  if (employeeCount <= 49) {
    return 3;
  }
  if (employeeCount <= 70) {
    return 4;
  }
  if (employeeCount <= 149) {
    return 5;
  }
  if (employeeCount <= 249) {
    return 6;
  }
  if (employeeCount <= 500) {
    return 7;
  }

  return 8;
};

const showModal = ref(false);
const isLoading = ref(false);
const tierNumber = ref<number>();
const companyId = ref<string>();
const currentPlanId = ref<string>();
const currentUserId = ref<string>();
const allPackagesAtTier = ref<PricingPackage[]>([]);

const setupHook = () => {
  const { tierNumber: tierNumberFromCurrentPlan, currentPlan } =
    useCurrentPlan();
  const { companyId: companyIdFromCurrentCompany } = useCurrentCompany();
  const { currentUser } = useCurrentUser();
  const { allStaff } = useStaffManagement();
  const { isBambeeLite } = usePlanAccess(true);

  companyId.value = companyIdFromCurrentCompany.value;
  tierNumber.value =
    tierNumberFromCurrentPlan.value ??
    mapEmployeeCountToTier(allStaff.value.length);
  currentPlanId.value = currentPlan.value?.value;
  currentUserId.value = currentUser.value?._id;
  isLoading.value = true;

  const productsToShow = isBambeeLite.value
    ? [ProductKey.Lite, ProductKey.Core]
    : [ProductKey.Core, ProductKey.Elite];

  const { onResult, onError } = useApolloQuery(useGetPricingPackagesQuery, {
    input: { companyId: companyId.value },
  });

  onResult((result: any) => {
    const res = result?.getPricingPackages;
    allPackagesAtTier.value = res?.packages as PricingPackage[];
    isLoading.value = false;
  });

  onError((error) => {
    console.error('error', error);
    isLoading.value = false;
  });
};

export interface PricePoint {
  billingPeriod: BillingPeriod;
  chargeCycle: string;
  monthlyPrice: number;
  planId: string;
  billingFeature?: BillingFeature;
}

type PlanNameToPricePoints = {
  [key in ProductKey]?: PricePoint[];
};

const mapPlanToPricePoints = computed(() => {
  const result: PlanNameToPricePoints = {};

  // Iterate through all packages and their plans
  allPackagesAtTier.value?.forEach((pkg) => {
    pkg?.plans?.forEach((plan) => {
      if (!plan) {
        return;
      }

      if (!result[plan.productKey]) {
        result[plan.productKey] = [
          {
            billingPeriod: plan.billingPeriod,
            chargeCycle: getChargeCycleForBillingPeriod(plan.billingPeriod),
            monthlyPrice: plan.monthlyPrice,
            planId: plan.value,
            billingFeature: plan.billingFeature ?? undefined,
          },
        ];
      } else {
        result[plan.productKey]?.push({
          billingPeriod: plan.billingPeriod,
          chargeCycle: getChargeCycleForBillingPeriod(plan.billingPeriod),
          monthlyPrice: plan.monthlyPrice,
          planId: plan.value,
          billingFeature: plan.billingFeature ?? undefined,
        });
      }
    });
  });

  return result;
});

const mapPlanToMonthlyPrice = computed(() => {
  const result: Record<string, any> = {};

  // Iterate through all packages and their plans
  allPackagesAtTier.value?.forEach((pkg) => {
    pkg?.plans?.forEach((plan) => {
      if (!plan) {
        return;
      }

      result[plan.productKey] = {
        billingPeriod: plan.billingPeriod,
        monthlyPrice: plan.monthlyPrice,
        priceId: plan.priceId,
        planId: plan.value,
        billingFeature: plan.billingFeature ?? undefined,
      };
    });
  });

  return result;
});

const toggleModal = () => {
  showModal.value = !showModal.value;
};

const isPurchasing = ref(false);
const subscribeToPlan = async (planId: string, threadId?: string) => {
  const { addSuccess, addError } = useNotifications();
  if (!companyId.value) {
    addError('Failed to upgrade plan');

    return false;
  }

  // Set loading state immediately
  isPurchasing.value = true;

  const { mutate, onDone, onError } = useApolloMutation(
    useUpgradeCorePlanMutation
  );

  mutate({
    input: {
      newPlanId: planId,
      companyId: companyId.value,
    },
  });

  onDone(({ upgradeCorePlan: res }) => {
    if (res) {
      addSuccess('Your plan has been updated!');

      // TODO: This modal will eventually be used outside of just guidance
      bam.trackEvent(HrGuidanceEvents.SELF_SERVE_PLAN_UPGRADE_SUCCESS, {
        currentPlanId: currentPlanId.value,
        newPlanId: planId,
      });

      if (threadId) {
        saveInLocalStorage(
          `${ELITE_PURCHASED_DURING_THREAD_KEY}:${threadId}`,
          true
        );
      }

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      isPurchasing.value = false;
    }

    return false;
  });

  onError((error) => {
    bam.trackEvent(HrGuidanceEvents.SELF_SERVE_PLAN_UPGRADE_ERROR, {
      currentPlanId: currentPlanId.value,
      newPlanId: planId,
    });
    addError(`Failed to upgrade plan : ${error}`);
    isPurchasing.value = false;
  });

  return false;
};

const usePlanUpgrade = (force = false) => {
  if (!init || force) {
    setupHook();
    init = true;
  }

  return {
    isLoading,
    isPurchasing,
    allPackagesAtTier,
    mapPlanToPricePoints,
    mapPlanToMonthlyPrice,
    showModal,
    toggleModal,
    subscribeToPlan,
  };
};

export default usePlanUpgrade;
